// OIDC-Anbindung an Keycloak (Realm trasaflow): Authorization Code + PKCE (S256), Silent Renew per
// Refresh Token, Logout über das End-Session-Endpoint. Kein offline_access im Web (siehe Plan, Auth).
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'
import i18n from '@/i18n'

const origin = window.location.origin

type RuntimeOidc = { authority?: string; clientId?: string }

/**
 * Laufzeitkonfiguration aus /config.json (Chart trasaflow, `oidc.authority`/`oidc.clientId`): Ein Image läuft so in
 * jeder Umgebung. Synchron, weil der UserManager synchron entsteht; die Datei ist klein und kommt vom eigenen Origin.
 * Fehlt sie oder ist sie kein JSON (lokal, Tests), gelten die Build-Variablen.
 */
function runtimeOidc(): RuntimeOidc {
  try {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', '/config.json', false)
    xhr.send()
    if (xhr.status !== 200) return {}
    const parsed = JSON.parse(xhr.responseText) as { oidc?: RuntimeOidc }
    return parsed.oidc ?? {}
  } catch {
    return {}
  }
}

export function createUserManager(): UserManager {
  const runtime = runtimeOidc()
  return new UserManager({
    authority:
      runtime.authority ??
      import.meta.env.VITE_OIDC_AUTHORITY ??
      'http://localhost:8180/realms/trasaflow',
    client_id: runtime.clientId ?? import.meta.env.VITE_OIDC_CLIENT_ID ?? 'trasaflow-web',
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: `${origin}/login?loggedOut=1`,
    response_type: 'code', // PKCE (S256) ist bei Code-Flow in oidc-client-ts immer aktiv
    scope: 'openid profile email',
    // Tokens nur in sessionStorage: überleben keinen Tab-Schluss und sind nicht tabübergreifend lesbar.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    // Erneuert kurz vor Ablauf (Access-Token lebt 300 s) per Refresh-Token-Grant, kein Iframe.
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 60,
    monitorSession: false,
    loadUserInfo: false,
  })
}

let manager: UserManager | undefined

export function userManager(): UserManager {
  manager ??= createUserManager()
  return manager
}

export async function getUser(): Promise<User | null> {
  const user = await userManager().getUser()
  return user && !user.expired ? user : null
}

export async function getAccessToken(): Promise<string | null> {
  const user = await getUser()
  return user?.access_token ?? null
}

/** Merkt sich das Ziel, damit der Nutzer nach dem Login dort landet, wo er hinwollte. */
export function login(returnTo = '/'): Promise<void> {
  // ui_locales: die Anmeldeseite von Keycloak erscheint in der Sprache des Portals (hr, de, en); ein Sprachwechsel
  // im Portal gilt beim nächsten Login.
  return userManager().signinRedirect({
    state: { returnTo },
    ui_locales: i18n.resolvedLanguage ?? i18n.language,
  })
}

/**
 * Passkey (WebAuthn, passwordless) einrichten: Keycloak führt nach der Anmeldung die Aktion
 * `webauthn-register-passwordless` aus (App-Initiated Action) und kehrt zum Portal zurück.
 */
export const PASSKEY_ACTION = 'webauthn-register-passwordless'

export function startPasskeySetup(returnTo = '/'): Promise<void> {
  return userManager().signinRedirect({
    state: { returnTo },
    ui_locales: i18n.resolvedLanguage ?? i18n.language,
    extraQueryParams: { kc_action: PASSKEY_ACTION },
  })
}

export async function logout(): Promise<void> {
  await userManager().signoutRedirect()
}

export type SessionEndReason = 'expired'
type Listener = (reason: SessionEndReason) => void
const listeners = new Set<Listener>()

/** Meldet, wenn die Sitzung nicht mehr erneuert werden kann (Refresh fehlgeschlagen, 401 vom Server). */
export function onSessionEnded(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function notifySessionEnded(reason: SessionEndReason = 'expired'): void {
  listeners.forEach((listener) => listener(reason))
}

export function watchSession(): () => void {
  const events = userManager().events
  const onError = () => {
    void userManager()
      .removeUser()
      .finally(() => notifySessionEnded('expired'))
  }
  events.addSilentRenewError(onError)
  events.addUserSignedOut(onError)
  return () => {
    events.removeSilentRenewError(onError)
    events.removeUserSignedOut(onError)
  }
}
